import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardHeader,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import BlockIcon from '@mui/icons-material/Block'
import CategoryIcon from '@mui/icons-material/Category'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import KpiCard from '../AppointmentDeliveriesV2/shared/KpiCard'
import {
  apiConstants,
  getGeneric,
  postGeneric,
  putGeneric,
} from '../../common/constants'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalise free-text into the format the backend stores (UPPER_SNAKE).
 * The server normalises on save too, so this preview matches what'll
 * actually be persisted.
 */
const normalise = (s = '') =>
  String(s)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

const NEW_CATEGORY_SENTINEL = '__NEW__'

// A reason can be linked to reasons of other categories (e.g. a reply to the requests it answers), stored as
// CATEGORY:CODE in linkedCodes; ANY links it to every reason.
const ANY_REASON = 'ANY'
const reasonKey = (r) => `${r.category}:${r.code}`

// ---------------------------------------------------------------------------
// Add / Edit form (inline modal-like card)
// ---------------------------------------------------------------------------

function ReasonFormCard({ category, onAdd, onValidationError }) {
  const [code, setCode] = useState('')
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [displayOrder, setDisplayOrder] = useState('')

  const submit = () => {
    const c = normalise(code)
    const l = label.trim()
    if (!c || !l) {
      onValidationError('Code and Display Label are both required')
      return
    }
    onAdd({
      code: c,
      label: l,
      description: description.trim() || undefined,
      displayOrder: displayOrder === '' ? undefined : Number(displayOrder),
      isActive: true,
    })
    setCode('')
    setLabel('')
    setDescription('')
    setDisplayOrder('')
  }

  const codePreview = normalise(code)

  return (
    <Box sx={{ p: 2, borderTop: '2px dashed #ddd', bgcolor: '#fafafa' }}>
      <Typography variant='caption' fontWeight={700} sx={{ color: '#1565c0', display: 'block', mb: 1 }}>
        Add new reason to <Box component='span' sx={{ fontFamily: 'monospace' }}>{category || '(pick category)'}</Box>
      </Typography>
      <Stack spacing={1}>
        <TextField
          size='small'
          fullWidth
          placeholder='Code (e.g., DELIVERY_REFUSED)'
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputProps={{ style: { fontFamily: 'monospace' } }}
          helperText={code && codePreview !== code ? `Will be saved as: ${codePreview}` : ' '}
        />
        <TextField
          size='small'
          fullWidth
          placeholder='Display label (what users see in the dropdown)'
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <TextField
          size='small'
          fullWidth
          placeholder='Description (optional)'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={1}
          maxRows={3}
        />
        <TextField
          size='small'
          sx={{ maxWidth: 200 }}
          type='number'
          placeholder='Display order (optional)'
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
        />
        <Button
          variant='contained'
          size='small'
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-start' }}
          onClick={submit}
          disabled={!category}
        >
          Add Reason
        </Button>
      </Stack>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ReasonMasterSettings() {
  const { enqueueSnackbar } = useSnackbar()

  const [categories, setCategories] = useState([])
  const [selectedCategory, setSelectedCategory] = useState('')
  const [reasons, setReasons] = useState([])
  const [showInactive, setShowInactive] = useState(false)
  // Two independent reload counters so refreshing the reason list after a write
  // doesn't also re-fetch categories and risk yanking `selectedCategory` from
  // under the user (the categories load defaulted to `list[0]` on first run).
  const [reloadCategories, setReloadCategories] = useState(0)
  const [reloadReasons, setReloadReasons] = useState(0)
  const [newCategoryDraft, setNewCategoryDraft] = useState('')
  const [allReasons, setAllReasons] = useState([])

  // Bump both — used by the top-right refresh icon. Bumping just one is the
  // common case after a CRUD action and is handled inside each handler.
  const reloadAll = () => {
    setReloadCategories((r) => r + 1)
    setReloadReasons((r) => r + 1)
  }

  // -- Load categories ------------------------------------------------------
  // Runs once on mount and again only when the user explicitly hits refresh or
  // a write created a brand-new category. Default-select only when no category
  // is selected yet — never overwrite the user's choice.
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getGeneric(apiConstants.REASON_MASTER_CATEGORIES, {})
        const list = Array.isArray(res?.data) ? res.data : res?.data?.responseObj || []
        setCategories(list)
        setSelectedCategory((current) => {
          if (current && current !== NEW_CATEGORY_SENTINEL) return current
          return list.length > 0 ? list[0] : current
        })
      } catch (e) {
        enqueueSnackbar(
          e?.response?.data?.message || 'Failed to load categories',
          { variant: 'error' },
        )
      }
    }
    loadCategories()
  }, [reloadCategories, enqueueSnackbar])

  // -- Load reasons for selected category ----------------------------------
  useEffect(() => {
    if (!selectedCategory || selectedCategory === NEW_CATEGORY_SENTINEL) {
      setReasons([])
      return
    }
    const loadReasons = async () => {
      try {
        const params = { category: selectedCategory, activeOnly: showInactive ? 'false' : 'true' }
        const res = await getGeneric(apiConstants.REASON_MASTER, params)
        const list = Array.isArray(res?.data) ? res.data : res?.data?.responseObj || []
        setReasons(list)
      } catch (e) {
        setReasons([])
        enqueueSnackbar(
          e?.response?.data?.message || 'Failed to load reasons',
          { variant: 'error' },
        )
      }
    }
    loadReasons()
  }, [selectedCategory, showInactive, reloadReasons, enqueueSnackbar])

  // -- All reasons, for the "Linked reasons" picker -----------------------
  useEffect(() => {
    const loadAll = async () => {
      try {
        const res = await getGeneric(`${apiConstants.REASON_MASTER}/all`, {})
        setAllReasons(Array.isArray(res?.data) ? res.data : res?.data?.responseObj || [])
      } catch (e) {
        setAllReasons([])
      }
    }
    loadAll()
  }, [reloadReasons])

  // other categories' active reasons, plus ANY; label shown as "CATEGORY · Label"
  const linkOptions = useMemo(() => [ANY_REASON, ...allReasons
    .filter((r) => r.isActive !== false && r.category !== selectedCategory)
    .map(reasonKey)], [allReasons, selectedCategory])
  const linkLabels = useMemo(() => {
    const m = { [ANY_REASON]: 'Any reason' }
    allReasons.forEach((r) => { m[reasonKey(r)] = `${r.category} · ${r.label}` })
    return m
  }, [allReasons])

  // -- Derived counts ------------------------------------------------------
  const totalActive = useMemo(() => reasons.filter((r) => r.isActive !== false).length, [reasons])
  const totalInactive = useMemo(() => reasons.filter((r) => r.isActive === false).length, [reasons])

  // -- Handlers ------------------------------------------------------------
  const handleCategoryChange = (value) => {
    if (value === NEW_CATEGORY_SENTINEL) {
      setSelectedCategory(NEW_CATEGORY_SENTINEL)
      return
    }
    setSelectedCategory(value)
    setNewCategoryDraft('')
  }

  const handleAddReason = async (payload) => {
    const targetCategory =
      selectedCategory === NEW_CATEGORY_SENTINEL ? normalise(newCategoryDraft) : selectedCategory
    if (!targetCategory) {
      enqueueSnackbar('Category is required', { variant: 'warning' })
      return
    }
    try {
      await postGeneric(apiConstants.REASON_MASTER, { ...payload, category: targetCategory })
      enqueueSnackbar('Reason added', { variant: 'success' })
      // If this created a brand-new category, switch to it and refresh the
      // categories list so the dropdown picks it up.
      if (selectedCategory === NEW_CATEGORY_SENTINEL) {
        setSelectedCategory(targetCategory)
        setNewCategoryDraft('')
        setReloadCategories((r) => r + 1)
      }
      setReloadReasons((r) => r + 1)
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Failed to add reason. Admin role required.',
        { variant: 'error' },
      )
    }
  }

  const handleToggleActive = async (id, isActive) => {
    try {
      await putGeneric(`${apiConstants.REASON_MASTER}/${id}`, { isActive })
      enqueueSnackbar(`Reason ${isActive ? 'enabled' : 'disabled'}`, { variant: 'success' })
      setReloadReasons((r) => r + 1)
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Failed to update reason',
        { variant: 'error' },
      )
    }
  }

  const handleUpdateField = async (id, patch) => {
    try {
      await putGeneric(`${apiConstants.REASON_MASTER}/${id}`, patch)
      enqueueSnackbar('Updated', { variant: 'success' })
      setReloadReasons((r) => r + 1)
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Failed to update reason',
        { variant: 'error' },
      )
    }
  }

  const isNewCategoryMode = selectedCategory === NEW_CATEGORY_SENTINEL
  const effectiveCategory = isNewCategoryMode ? normalise(newCategoryDraft) : selectedCategory

  // ------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------
  return (
    <>
      <PageTitle>Reason Master</PageTitle>

      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='body2' color='text.secondary'>
          Manage dropdown reasons for RTO Request, Cancellation, NDR and similar flows. Reasons are
          grouped by free-text category and exposed to UI dropdowns via{' '}
          <Box component='code' sx={{ fontFamily: 'monospace', fontSize: 12, bgcolor: '#f0f0f0', px: 0.5 }}>
            /api/reason-master?category=…
          </Box>
          .
        </Typography>
        <IconButton onClick={reloadAll}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <KpiCard
            color='blue'
            label='Active in this category'
            value={totalActive}
            sub='Visible to users'
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard
            color='grey'
            label='Inactive in this category'
            value={totalInactive}
            sub='Hidden from dropdowns'
            icon={<BlockIcon />}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiCard
            color='amber'
            label='Total categories'
            value={categories.length}
            sub='Distinct reason groups'
            icon={<CategoryIcon />}
          />
        </Grid>
      </Grid>

      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
              <TuneOutlinedIcon sx={{ color: '#1565c0' }} />
              <FormControl size='small' sx={{ minWidth: 260 }}>
                <InputLabel id='reason-category-label'>Category</InputLabel>
                <Select
                  labelId='reason-category-label'
                  label='Category'
                  value={selectedCategory || ''}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                >
                  {categories.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                  <MenuItem value={NEW_CATEGORY_SENTINEL}>
                    <em>+ New Category…</em>
                  </MenuItem>
                </Select>
              </FormControl>

              {isNewCategoryMode && (
                <TextField
                  size='small'
                  placeholder='NEW_CATEGORY_NAME'
                  value={newCategoryDraft}
                  onChange={(e) => setNewCategoryDraft(e.target.value)}
                  helperText={
                    newCategoryDraft && normalise(newCategoryDraft) !== newCategoryDraft
                      ? `Will be saved as: ${normalise(newCategoryDraft)}`
                      : 'New categories are created when the first reason in them is saved.'
                  }
                  sx={{ minWidth: 280 }}
                  inputProps={{ style: { fontFamily: 'monospace' } }}
                />
              )}

              <FormControl size='small'>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    size='small'
                  />
                  <Typography variant='caption'>Show inactive</Typography>
                </Stack>
              </FormControl>
            </Stack>
          }
        />

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ width: 40 }}>#</TableCell>
                <TableCell>
                  <strong>Code</strong>
                </TableCell>
                <TableCell>
                  <strong>Display Label</strong>
                </TableCell>
                <TableCell>
                  <strong>Description</strong>
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <strong>Linked reasons</strong>
                </TableCell>
                <TableCell align='center' sx={{ width: 110 }}>
                  <strong>Display Order</strong>
                </TableCell>
                <TableCell align='center' sx={{ width: 80 }}>
                  <strong>Active</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reasons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align='center' sx={{ color: 'text.secondary', py: 3 }}>
                    {isNewCategoryMode
                      ? 'Type a new category name above, then add your first reason.'
                      : 'No reasons in this category yet. Use the form below.'}
                  </TableCell>
                </TableRow>
              )}
              {reasons.map((r, idx) => (
                <TableRow key={r.id || r.code} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography variant='caption' fontFamily='monospace'>
                      {r.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      variant='standard'
                      fullWidth
                      defaultValue={r.label}
                      onBlur={(e) => {
                        const v = e.target.value.trim()
                        if (v && v !== r.label) handleUpdateField(r.id, { label: v })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size='small'
                      variant='standard'
                      fullWidth
                      defaultValue={r.description || ''}
                      onBlur={(e) => {
                        const v = e.target.value.trim()
                        if (v !== (r.description || '')) handleUpdateField(r.id, { description: v })
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      size='small'
                      options={linkOptions}
                      value={r.linkedCodes || []}
                      getOptionLabel={(k) => linkLabels[k] || k}
                      filterSelectedOptions
                      onChange={(_e, v) => handleUpdateField(r.id, { linkedCodes: v })}
                      renderInput={(p) => <TextField {...p} variant='standard' placeholder={(r.linkedCodes || []).length ? '' : 'None'} />}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <TextField
                      size='small'
                      variant='standard'
                      type='number'
                      defaultValue={r.displayOrder ?? ''}
                      sx={{ width: 70 }}
                      onBlur={(e) => {
                        const v = e.target.value === '' ? null : Number(e.target.value)
                        if (v !== (r.displayOrder ?? null))
                          handleUpdateField(r.id, { displayOrder: v })
                      }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    {/* Toggle covers both directions — deactivate (off) and
                        reactivate (on). Replaced the separate "Deactivate" icon
                        button since it produced identical state. */}
                    <Switch
                      checked={r.isActive !== false}
                      onChange={(e) => handleToggleActive(r.id, e.target.checked)}
                      size='small'
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <ReasonFormCard
          category={effectiveCategory}
          onAdd={handleAddReason}
          onValidationError={(msg) => enqueueSnackbar(msg, { variant: 'warning' })}
        />
      </Card>
    </>
  )
}
